import { useEffect } from 'react';

import { TagDTO, TagEntity } from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { WorkOrderSettingsSkeleton } from '@/components/settings/settings-form-skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useToast } from '@/components/ui/toaster';
import { usePermissions } from '@/hooks/use-permissions';
import { getTagsQueryOptions, tagKeys } from '@/queries/assets';
import { ApiService } from '@/services/api-v2';

import WorkOrderTags from './work-order-tags';

const workOrderTagsFormSchema = z.object({
  tags: z.array(z.object({ id: z.string(), tag: z.string(), order: z.number().nullable() })),
});

type WorkOrderTagsFormData = z.infer<typeof workOrderTagsFormSchema>;

function WorkOrderTagsForm({ defaultValues }: { defaultValues: WorkOrderTagsFormData }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { canEditWorkspaceSettings } = usePermissions();

  const {
    watch,
    setValue,
    handleSubmit,
    reset,
    formState: { isDirty, isSubmitting },
  } = useForm<WorkOrderTagsFormData>({
    resolver: zodResolver(workOrderTagsFormSchema),
    defaultValues,
  });

  const formData = watch();

  // react-hook-form only clears `isDirty` on reset, not on submit — re-baseline once the
  // query refetches post-save so the Save button disables again.
  useEffect(() => {
    reset({ tags: defaultValues.tags });
  }, [defaultValues.tags, reset]);

  const { mutateAsync: updateWorkOrderTags } = useMutation({
    mutationFn: async ({ tags }: { tags: TagDTO[] }) =>
      await ApiService.UpdateTags({
        tags,
        entity: TagEntity.WorkOrder,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tagKeys.list(TagEntity.WorkOrder, false) });
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  function handleTagsChange(tags: TagDTO[]) {
    setValue('tags', tags, { shouldDirty: true });
  }

  async function onSubmit(data: WorkOrderTagsFormData) {
    try {
      await updateWorkOrderTags({
        tags: data.tags.map((tag, index) => ({ ...tag, order: index })),
      });
      toast.success('Work order settings updated successfully');
    } catch {
      // Do nothing, errors are handled by the mutation
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
      <WorkOrderTags onTagsChange={handleTagsChange} workOrderTags={formData.tags} disabled={!canEditWorkspaceSettings} />
      {canEditWorkspaceSettings && (
        <Button type="submit" variant="success" className="w-fit" disabled={!isDirty || isSubmitting}>
          Save changes
        </Button>
      )}
    </form>
  );
}

function SettingsWorkOrdersPage() {
  const { data: workOrderTags } = useQuery(getTagsQueryOptions({ entity: TagEntity.WorkOrder }));

  return (
    <div className="flex w-full max-w-[833px] flex-col gap-4 rounded-lg bg-white p-6 shadow-lg">
      <ScrollArea viewportClassName="pr-2">
        <div className="flex flex-col gap-4">
          <h1 className="text-default-text text-xl font-medium">Work Orders</h1>
          {workOrderTags ? <WorkOrderTagsForm defaultValues={{ tags: workOrderTags }} /> : <WorkOrderSettingsSkeleton />}
        </div>
      </ScrollArea>
    </div>
  );
}

export default SettingsWorkOrdersPage;
